import type { ReactNode, CSSProperties } from "react";

/**
 * Heading system (DS core) — one canonical set of heading+subheading components
 * so spacing, sizes, eyebrow and wrapping are identical everywhere. Three tiers:
 *
 *   PageHeading    — page hero (h1).   tone: blue gradient | light
 *   SectionHeading — block header (h2). align: start | center · tone: light | dark
 *   CardHeading    — card/bento title (h3). size: md | sm · tone: light | dark
 *
 * Balanced wrapping is built in: `text-balance` on the heading, `text-pretty` on
 * the intro/body (Framer's "Balance" — pure CSS, no JS). Replaces the scattered
 * inline headings (off-scale 32/40/48px, text-3xl/2xl, mixed leading/spacing).
 */

const SHELL = "mx-auto w-full max-w-content px-4 md:px-10";
const BLUE_GRAD = "linear-gradient(to bottom, var(--ds-accent-deep), var(--ds-accent))";
const HERO_GRID: CSSProperties = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
  backgroundSize: "64px 64px",
  maskImage: "linear-gradient(to bottom, black, transparent)",
  WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
};

const EYEBROW = "font-display text-[12px] font-semibold uppercase tracking-[0.14em]";

/* ── PageHeading (h1) ───────────────────────────────────────────────────── */

export function PageHeading({
  eyebrow,
  title,
  intro,
  actions,
  tone = "blue",
  align = "start",
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  intro?: ReactNode;
  actions?: ReactNode;
  tone?: "blue" | "light";
  align?: "start" | "center";
}) {
  const dark = tone === "blue";
  const centered = align === "center";
  const inner = (
    <div className={centered ? "mx-auto flex max-w-3xl flex-col items-center text-center" : ""}>
      {eyebrow && <p className={`${EYEBROW} ${dark ? "text-white/70" : "text-ink-faint"}`}>{eyebrow}</p>}
      <h1 className={`mt-5 max-w-[820px] text-balance font-display text-ds-38 font-semibold leading-[1.08] tracking-[-1px] sm:text-ds-46 lg:text-[56px] ${dark ? "text-white" : "text-ds-ink"}`}>
        {title}
      </h1>
      {intro && (
        <p className={`mt-5 max-w-[640px] text-pretty text-ds-18 leading-relaxed ${dark ? "text-white/80" : "text-ds-ink-soft"}`}>
          {intro}
        </p>
      )}
      {actions && <div className={`mt-7 flex flex-wrap items-center gap-3 ${centered ? "justify-center" : ""}`}>{actions}</div>}
    </div>
  );

  if (dark) {
    return (
      <section className="bg-bg font-ds">
        <div className="px-4 py-3">
          <div className="relative overflow-hidden rounded-[32px]" style={{ background: BLUE_GRAD }}>
            <div className="pointer-events-none absolute inset-0" style={HERO_GRID} />
            <div className={`relative py-16 md:py-24 ${SHELL}`}>{inner}</div>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="bg-bg-subtle font-ds">
      <div className={`py-16 md:py-20 ${SHELL}`}>{inner}</div>
    </section>
  );
}

/* ── SectionHeading (h2) ────────────────────────────────────────────────── */

export function SectionHeading({
  eyebrow,
  title,
  intro,
  align = "start",
  tone = "light",
  size = "md",
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  intro?: ReactNode;
  align?: "start" | "center";
  tone?: "light" | "dark";
  /** md = block header (ds-30→34, default) · lg = statement section (ds-38→46). */
  size?: "md" | "lg";
}) {
  const dark = tone === "dark";
  const centered = align === "center";
  const sizeCls = size === "lg" ? "text-ds-38 sm:text-ds-46" : "text-ds-30 lg:text-ds-34";
  return (
    <div className={centered ? "flex flex-col items-center text-center" : ""}>
      {eyebrow && <p className={`${EYEBROW} ${dark ? "text-white/70" : "text-ink-faint"}`}>{eyebrow}</p>}
      <h2 className={`mt-3 max-w-2xl text-balance font-display font-semibold leading-[1.12] tracking-[-0.5px] ${sizeCls} ${dark ? "text-white" : "text-ds-ink"}`}>
        {title}
      </h2>
      {intro && (
        <p className={`mt-4 max-w-xl text-pretty text-ds-18 leading-relaxed ${dark ? "text-white/80" : "text-ds-ink-soft"}`}>
          {intro}
        </p>
      )}
    </div>
  );
}

/* ── CardHeading (h3) ───────────────────────────────────────────────────── */

export function CardHeading({
  title,
  body,
  size = "md",
  tone = "light",
  align = "start",
}: {
  title: ReactNode;
  body?: ReactNode;
  size?: "md" | "sm";
  tone?: "light" | "dark";
  align?: "start" | "center";
}) {
  const dark = tone === "dark";
  const center = align === "center" ? "text-center" : "";
  return (
    <>
      <h3 className={`text-balance font-semibold tracking-tight ${center} ${size === "md" ? "text-ds-22" : "text-ds-18"} ${dark ? "text-white" : "text-ds-ink"}`}>
        {title}
      </h3>
      {body && (
        <p className={`mt-2 text-pretty text-ds-14 leading-relaxed ${center} ${dark ? "text-white/80" : "text-ds-ink-soft"}`}>
          {body}
        </p>
      )}
    </>
  );
}
